import {
  CheckIcon,
  ChevronDownIcon,
  PlusIcon,
} from '@langchain/untitled-ui-icons';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
} from '@mui/joy';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import { Button } from '@/design-system/components/Button/Button';
import { GroupedTabs } from '@/design-system/components/GroupedTabs/GroupedTabs';
import { Text } from '@/design-system/components/Text/Text';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useNewApiKeyMutation,
  useNewOrgScopedApiKeyMutation,
  useNewOrgScopedPatMutation,
  useOrgWorkspaces,
  usePermissionsList,
  useRoles,
} from '@/hooks/useSwr.tsx';
import {
  NewApiKeySchema,
  OrganizationRoleSchema,
  TenantSchema,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { extractGroups } from '../utils/organizationMembers.utils';
import { EditRoleDrawer } from './EditRoleDrawer';

type ExpirationOption = 'Never' | '30d' | '90d' | '1y' | 'Custom';
type ScopeOption = 'full' | 'specific';

type CreateKeyFormData = {
  description: string;
  type: 'pat' | 'service';
  expires_at?: string | null;
  scope?: 'full' | 'specific';
  workspaces?: string[];
  role_id?: string;
  org_role_id?: string;
  default_workspace_id?: string;
};

type ApiKeyPayload = {
  description: string;
  expires_at?: string | null;
  workspaces?: string[];
  role_id?: string;
  org_role_id?: string;
  default_workspace_id?: string;
};

function WorkspaceSelect(props: {
  disabled?: boolean;
  value: TenantSchema[];
  onChange: (value: TenantSchema[]) => void;
}) {
  const { data, isLoading } = useOrgWorkspaces();
  const containerRef = useRef<HTMLButtonElement>(null);
  const [visibleWorkspaces, setVisibleWorkspaces] = useState<{
    visible: TenantSchema[];
    overflow: number;
  }>({ visible: props.value, overflow: 0 });

  const filteredWorkspaces =
    data?.filter((workspace) => {
      return workspace.permissions?.includes('workspaces:manage') ?? false;
    }) ?? [];

  useEffect(() => {
    const calculateVisibleWorkspaces = () => {
      if (!containerRef.current || props.value.length === 0) {
        setVisibleWorkspaces({ visible: props.value, overflow: 0 });
        return;
      }

      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerPadding = 20; // Account for px-2.5 padding
      const availableWidth = containerWidth - containerPadding;
      const chipGap = 4; // gap-1
      const overflowChipWidth = 40; // Approximate width of "+ X..." chip

      let currentWidth = 0;
      const visible: TenantSchema[] = [];

      for (const workspace of props.value) {
        // Create a temporary element to measure actual text width
        const tempSpan = document.createElement('span');
        tempSpan.style.visibility = 'hidden';
        tempSpan.style.position = 'absolute';
        tempSpan.style.fontSize = '14px'; // text-sm
        tempSpan.style.fontWeight = '400';
        tempSpan.textContent = workspace.display_name;
        document.body.appendChild(tempSpan);

        const textWidth = tempSpan.offsetWidth;
        document.body.removeChild(tempSpan);

        const chipWidth = textWidth + 12; // px-1.5 padding = 6px each side

        const wouldNeedOverflowChip = visible.length < props.value.length - 1;
        const overflowChipSpace = wouldNeedOverflowChip
          ? overflowChipWidth + chipGap
          : 0;

        if (currentWidth + chipWidth + overflowChipSpace <= availableWidth) {
          visible.push(workspace);
          currentWidth += chipWidth + chipGap;
        } else {
          break;
        }
      }

      setVisibleWorkspaces({
        visible,
        overflow: props.value.length - visible.length,
      });
    };

    calculateVisibleWorkspaces();

    // Recalculate on window resize
    window.addEventListener('resize', calculateVisibleWorkspaces);
    return () =>
      window.removeEventListener('resize', calculateVisibleWorkspaces);
  }, [props.value]);

  if (isLoading) return null;
  return (
    <Popover>
      <PopoverTrigger
        ref={containerRef}
        disabled={props.disabled}
        className="flex h-[38px] flex-wrap gap-1 rounded-md border border-secondary p-2 px-2.5 text-left disabled:bg-secondary disabled:text-tertiary data-[placeholder]:text-tertiary"
      >
        {props.value.length > 0 ? (
          <>
            {visibleWorkspaces.visible.map((v) => (
              <span
                className="flex items-center gap-1 rounded-md border border-secondary px-1.5 text-sm"
                key={v.id}
              >
                <span>{v.display_name}</span>
              </span>
            ))}
            {visibleWorkspaces.overflow > 0 && (
              <span className="flex items-center gap-1 rounded-md border border-secondary bg-secondary px-1.5 text-sm">
                <span>+ {visibleWorkspaces.overflow}...</span>
              </span>
            )}
          </>
        ) : (
          <span className="text-sm text-tertiary">
            Select one or more workspaces
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[30vh] overflow-y-auto p-0"
        style={{
          width: 'var(--radix-popper-anchor-width)',
        }}
      >
        <Command>
          <CommandInput
            className="m-2 mb-0 px-2"
            placeholder="Search workspaces..."
          />
          <CommandList>
            <CommandGroup>
              {filteredWorkspaces.map((workspace) => {
                const selected = !!props.value.find(
                  (v) => v.id === workspace.id
                );
                return (
                  <CommandItem
                    className="relative pl-8"
                    key={workspace.id}
                    onSelect={() => {
                      const existingSelection = props.value.findIndex(
                        (v) => v.id === workspace.id
                      );
                      if (existingSelection > -1) {
                        props.onChange(
                          props.value.filter((_, i) => i !== existingSelection)
                        );
                      } else {
                        props.onChange([...props.value, workspace]);
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {selected && (
                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                          <CheckIcon className="h-4 w-4" />
                        </span>
                      )}
                      <Text variant="sm">{workspace.display_name}</Text>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandEmpty>
              <Text variant="sm">No workspaces found</Text>
            </CommandEmpty>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SingleWorkspaceSelect(props: {
  value: TenantSchema | undefined;
  onChange: (value: TenantSchema | undefined) => void;
}) {
  const { data, isLoading } = useOrgWorkspaces();

  if (isLoading) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="justify-between"
          variant="outlined"
          size="md"
          rightDecorator={ChevronDownIcon}
        >
          {props.value ? props.value.display_name : 'Select workspace...'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[300px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto"
      >
        {data?.map((workspace) => {
          const selected = props.value?.id === workspace.id;
          return (
            <DropdownMenuItem
              key={workspace.id}
              onSelect={() => {
                props.onChange(workspace);
              }}
              className="flex flex-row items-center gap-2"
            >
              <Text variant="sm">{workspace.display_name}</Text>
              {selected && <CheckIcon className="h-4 w-4" />}
            </DropdownMenuItem>
          );
        })}
        {(!data || data.length === 0) && (
          <DropdownMenuItem disabled>
            <Text variant="sm">No workspaces found</Text>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RoleSelect(props: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  scope: 'organization' | 'workspace';
  placeholder: string;
  disabled?: boolean;
  selectedOrgRole?: string;
  orgRoles?: OrganizationRoleSchema[];
  currentScope?: 'full' | 'specific';
}) {
  const rolesQuery = useRoles(props.scope);
  const roles = rolesQuery.data;
  const { authorize } = usePermissions(props.scope);

  const defaultRole = roles?.find((role) =>
    props.scope === 'organization'
      ? role.name === 'ORGANIZATION_USER'
      : role.name === 'WORKSPACE_USER'
  );

  const workspaceAdminRole = roles?.find(
    (role) => role.name === 'WORKSPACE_ADMIN'
  );

  const selectedOrgRoleData = props.orgRoles?.find(
    (role) => role.id === props.selectedOrgRole
  );
  const isOrgAdmin = selectedOrgRoleData?.name === 'ORGANIZATION_ADMIN';

  const { onChange, scope, currentScope, value, disabled, placeholder } = props;

  const filteredRoles = roles?.filter((role) => {
    if (props.scope === 'organization') {
      if (role.name === 'ORGANIZATION_USER') return true;

      if (role.name === 'ORGANIZATION_ADMIN') {
        return authorize('organization:manage');
      }
    } else {
      if (role.name === 'WORKSPACE_USER' || role.name === 'WORKSPACE_VIEWER')
        return true;

      if (role.name === 'WORKSPACE_ADMIN') {
        return authorize('workspaces:manage');
      }

      return authorize('workspaces:manage');
    }
  });

  useEffect(() => {
    // Set default role if no value is selected
    if (!value && defaultRole) {
      onChange(defaultRole.id);
      return;
    }

    // For workspace scope with full org access and org admin role, set workspace admin
    if (
      scope === 'workspace' &&
      currentScope === 'full' &&
      isOrgAdmin &&
      workspaceAdminRole &&
      value !== workspaceAdminRole.id
    ) {
      onChange(workspaceAdminRole.id);
      return;
    }

    // For workspace scope with specific workspaces, set default role
    if (
      scope === 'workspace' &&
      currentScope === 'specific' &&
      defaultRole &&
      !value
    ) {
      onChange(defaultRole.id);
    }
  }, [
    value,
    defaultRole,
    onChange,
    scope,
    currentScope,
    isOrgAdmin,
    workspaceAdminRole,
  ]);

  if (rolesQuery.isLoading) return null;

  return (
    <Select
      value={value}
      onValueChange={onChange}
      disabled={
        disabled ||
        (scope === 'workspace' && currentScope === 'full' && isOrgAdmin)
      }
    >
      <SelectTrigger className="rounded-md border border-secondary p-2 px-2.5 text-left data-[placeholder]:text-tertiary">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {filteredRoles?.map((role) => {
          return (
            <SelectItem key={role.id} value={role.id}>
              <div className="flex items-center gap-2">{role.display_name}</div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function CreateKeyForm(props: {
  serviceKeysEnabled: boolean;
  isLoading: boolean;
  onSubmit: (data: CreateKeyFormData) => void;
  onCancel: () => void;
}) {
  'use no memo';
  const [expirationOption, setExpirationOption] =
    useState<ExpirationOption>('Never');
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<TenantSchema[]>(
    []
  );
  const [selectedWorkspaceRole, setSelectedWorkspaceRole] = useState<
    string | undefined
  >(undefined);
  const [selectedOrgRole, setSelectedOrgRole] = useState<string | undefined>(
    undefined
  );
  const [selectedDefaultWorkspace, setSelectedDefaultWorkspace] = useState<
    TenantSchema | undefined
  >(undefined);

  const { authorize: authorizeOrg } = usePermissions('organization');
  const { authorize: authorizeWorkspace } = usePermissions('workspace');

  const authorize = useCallback(
    (permission: 'organization:manage' | 'workspaces:manage') => {
      if (permission === 'organization:manage') {
        return authorizeOrg(permission);
      } else {
        return authorizeWorkspace(permission);
      }
    },
    [authorizeOrg, authorizeWorkspace]
  );

  const [scopeOption, setScopeOption] = useState<ScopeOption>(() => {
    return authorize('organization:manage') ? 'full' : 'specific';
  });

  const orgRolesQuery = useRoles('organization');

  const selectedOrgRoleData = orgRolesQuery.data?.find(
    (role) => role.id === selectedOrgRole
  );
  const isOrgAdmin = selectedOrgRoleData?.name === 'ORGANIZATION_ADMIN';

  const isWorkspaceRoleDisabled = scopeOption === 'full' && isOrgAdmin;

  const permissions = usePermissionsList();
  const workspacePermissionsOnly = permissions.data?.filter(
    (p) => !p.name.startsWith('organization')
  );
  const permissionGroups = extractGroups(workspacePermissionsOnly);

  const { value: canUseRbac } = useOrgConfig(OrgConfigs.can_use_rbac);

  const form = useForm<{
    description: string;
    type: 'pat' | 'service';
    customDays?: number;
  }>({
    defaultValues: { type: 'pat' },
  });

  const calculateExpirationDate = (
    option: ExpirationOption,
    days?: number
  ): string | null => {
    if (option === 'Never') return null;

    const now = new Date();
    let daysToAdd = 0;

    switch (option) {
      case '30d':
        daysToAdd = 30;
        break;
      case '90d':
        daysToAdd = 90;
        break;
      case '1y':
        daysToAdd = 365;
        break;
      case 'Custom':
        daysToAdd = days || 0;
        break;
    }

    if (daysToAdd <= 0) return null;

    const expirationDate = new Date(
      now.getTime() + daysToAdd * 24 * 60 * 60 * 1000
    );
    return expirationDate.toISOString();
  };

  const handleSubmit = (data: {
    description: string;
    type: 'pat' | 'service';
    customDays?: number;
  }) => {
    const expires_at = calculateExpirationDate(
      expirationOption,
      data.customDays
    );

    const disabled =
      !data.description?.trim() ||
      (data.type == 'pat' && !selectedDefaultWorkspace) ||
      (data.type === 'service' &&
        scopeOption === 'specific' &&
        selectedWorkspaces.length === 0);

    if (disabled) return;

    const submitData: CreateKeyFormData = {
      description: data.description,
      type: data.type,
      expires_at,
      ...(data.type === 'service' && {
        scope: scopeOption,
        role_id: selectedWorkspaceRole,
        ...(scopeOption === 'full' && { org_role_id: selectedOrgRole }),
        ...(scopeOption === 'specific' && {
          workspaces: selectedWorkspaces.map((w) => w.id),
        }),
      }),
      ...(data.type === 'pat' &&
        selectedDefaultWorkspace && {
          default_workspace_id: selectedDefaultWorkspace.id,
        }),
    };

    props.onSubmit(submitData);
  };

  const expirationOptions = [
    { value: 'Never' as const, display: 'Never' },
    { value: '30d' as const, display: '30d' },
    { value: '90d' as const, display: '90d' },
    { value: '1y' as const, display: '1 year' },
    { value: 'Custom' as const, display: 'Custom' },
  ];

  const scopeOptions = [
    ...(authorize('organization:manage')
      ? [{ value: 'full' as const, display: 'Full Organization' }]
      : []),
    { value: 'specific' as const, display: 'Specific Workspaces' },
  ];

  return (
    <form
      className="flex flex-col items-stretch gap-8"
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <FormControl error={!!form.formState.errors.description?.message}>
        <FormLabel>Description</FormLabel>
        <Input
          placeholder="Description..."
          {...form.register('description', {
            required: 'Description is required',
          })}
        />
        {form.formState.errors.description?.message && (
          <FormHelperText>
            {form.formState.errors.description?.message}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl>
        <FormLabel>Key Type</FormLabel>
        <Controller
          control={form.control}
          name="type"
          rules={{ required: 'Key type is required' }}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              name="radio-buttons-group"
              className="flex flex-row gap-4"
              sx={{ flexDirection: 'row' }}
            >
              <div
                className={cn(
                  'relative w-1/2 cursor-pointer rounded-lg bg-transparent px-4 py-3 transition-colors duration-200 ease-in-out',
                  field.value === 'pat'
                    ? 'border-2 border-[var(--bg-brand-primary)]'
                    : 'border border-secondary hover:border-gray-400'
                )}
                onClick={() => field.onChange('pat')}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <span className="text-base text-sm font-medium">
                      Personal Access Token
                    </span>
                    <FormHelperText
                      id="pat-helper-text"
                      className="font-regular text-tertiary"
                    >
                      Used for authenticating with the API as an individual
                      user.
                    </FormHelperText>
                  </div>
                  <Radio
                    value="pat"
                    slotProps={{
                      input: { 'aria-describedby': 'pat-helper-text' },
                      root: {
                        sx: {
                          '& .MuiRadio-root': {
                            color: 'transparent',
                            border: '2px solid var(--bg-brand-primary)',
                            borderRadius: '50%',
                          },
                          '& .Mui-checked': {
                            color: 'var(--bg-brand-primary)',
                            '& .MuiSvgIcon-root': {
                              fill: 'white',
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
              {props.serviceKeysEnabled && (
                <div
                  className={cn(
                    'relative w-1/2 cursor-pointer rounded-lg bg-transparent px-4 py-3 transition-colors duration-200 ease-in-out',
                    field.value === 'service'
                      ? 'border-2 border-[var(--bg-brand-primary)]'
                      : 'border border-secondary hover:border-gray-400'
                  )}
                  onClick={() => field.onChange('service')}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className="text-base text-sm font-medium">
                        Service Key
                      </span>
                      <FormHelperText
                        id="service-helper-text"
                        className="font-regular text-tertiary"
                      >
                        Tied to a service principal for automation and CI
                        workflows.
                      </FormHelperText>
                    </div>
                    <Radio
                      value="service"
                      slotProps={{
                        input: { 'aria-describedby': 'service-helper-text' },
                        root: {
                          sx: {
                            '& .MuiRadio-root': {
                              color: 'transparent',
                              border: '2px solid var(--bg-brand-primary)',
                              borderRadius: '50%',
                            },
                            '& .Mui-checked': {
                              color: 'var(--bg-brand-primary)',
                              '& .MuiSvgIcon-root': {
                                fill: 'white',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}
            </RadioGroup>
          )}
        />
      </FormControl>

      {form.watch('type') === 'service' && (
        <FormControl>
          <FormLabel>Scope for Service Key</FormLabel>
          <div className="flex flex-col gap-3">
            <GroupedTabs
              options={scopeOptions}
              value={scopeOption}
              onChange={setScopeOption}
              className="transition-all duration-200 ease-in-out"
            />
            <div className="-mt-1 text-xs text-tertiary">
              {scopeOption === 'full'
                ? 'Grants access across all workspaces'
                : 'Only authorizes requests within selected workspaces'}
            </div>
          </div>
        </FormControl>
      )}

      {form.watch('type') === 'service' &&
        scopeOption === 'full' &&
        canUseRbac && (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Text variant="sm" weight="medium" as="label" className="pt-1">
                Assign Org Role
              </Text>
              <RoleSelect
                value={selectedOrgRole}
                onChange={setSelectedOrgRole}
                scope="organization"
                placeholder="Select org role..."
              />
              <div className="text-xs text-tertiary">
                Choose an organization role for the service key.
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                <Text variant="sm" weight="medium" as="label">
                  Assign Role
                </Text>
                <EditRoleDrawer
                  trigger={(onClick) => (
                    <Button variant="plain" onClick={onClick}>
                      + New Role
                    </Button>
                  )}
                  permissionGroups={permissionGroups}
                />
              </div>
              <RoleSelect
                value={selectedWorkspaceRole}
                onChange={setSelectedWorkspaceRole}
                scope="workspace"
                placeholder="Select workspace role..."
                disabled={isWorkspaceRoleDisabled}
                selectedOrgRole={selectedOrgRole}
                orgRoles={orgRolesQuery.data}
                currentScope={scopeOption}
              />
              <div className="mt-2 text-xs text-tertiary">
                Grant specific permissions to this key with a role.
              </div>
            </div>
          </div>
        )}

      {form.watch('type') === 'service' && scopeOption === 'specific' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Text variant="sm" weight="medium" as="label" className="pt-1">
              Workspaces
            </Text>
            <WorkspaceSelect
              value={selectedWorkspaces}
              onChange={setSelectedWorkspaces}
            />
            <div className="text-xs text-tertiary">
              Limit this API key's access to specific workspaces.
            </div>
          </div>

          {canUseRbac && (
            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                <Text variant="sm" weight="medium" as="label">
                  Assign Role
                </Text>
                <EditRoleDrawer
                  trigger={(onClick) => (
                    <Button variant="plain" onClick={onClick}>
                      + New Role
                    </Button>
                  )}
                  permissionGroups={permissionGroups}
                />
              </div>
              <RoleSelect
                value={selectedWorkspaceRole}
                onChange={setSelectedWorkspaceRole}
                scope="workspace"
                placeholder="Select workspace role..."
                disabled={isWorkspaceRoleDisabled}
                selectedOrgRole={selectedOrgRole}
                orgRoles={orgRolesQuery.data}
                currentScope={scopeOption}
              />
              <div className="mt-2 text-xs text-tertiary">
                Grant specific permissions to this key with a role.
              </div>
            </div>
          )}
        </div>
      )}

      {form.watch('type') === 'pat' && (
        <FormControl>
          <FormLabel>Default Workspace</FormLabel>
          <div className="flex flex-col gap-2">
            <SingleWorkspaceSelect
              value={selectedDefaultWorkspace}
              onChange={setSelectedDefaultWorkspace}
            />
            <Text variant="sm" className="text-tertiary">
              When no X-Tenant-ID header is provided, this workspace will be
              used as the default.
            </Text>
          </div>
        </FormControl>
      )}

      <FormControl
        error={
          expirationOption === 'Custom' &&
          !!form.formState.errors.customDays?.message
        }
      >
        <FormLabel>Expiration Date</FormLabel>
        <div className="flex flex-row items-center gap-4">
          <GroupedTabs
            options={expirationOptions}
            value={expirationOption}
            onChange={setExpirationOption}
            className="transition-all duration-200 ease-in-out"
          />

          {expirationOption === 'Custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                {...form.register('customDays', {
                  required:
                    expirationOption === 'Custom'
                      ? 'Please enter a valid number of days'
                      : false,
                  validate: (value) => {
                    if (expirationOption !== 'Custom') return true;
                    if (!value || value <= 0) {
                      return 'Please enter a valid number of days greater than 0';
                    }
                    return true;
                  },
                  valueAsNumber: true,
                })}
                slotProps={{
                  input: {
                    min: 1,
                    step: 1,
                  },
                }}
                sx={{
                  width: '80px',
                  '& input': {
                    boxShadow: 'none !important',
                    MozAppearance: 'textfield',
                  },
                  '& input::-webkit-outer-spin-button': {
                    WebKitAppearance: 'none !important',
                    margin: 0,
                  },
                  '& input::-webkit-inner-spin-button': {
                    WebKitAppearance: 'none !important',
                    margin: 0,
                  },
                }}
              />
              <span className="text-sm text-tertiary">days</span>
            </div>
          )}

          {expirationOption === 'Custom' &&
            form.formState.errors.customDays?.message && (
              <FormHelperText className="text-error-primary">
                {form.formState.errors.customDays?.message}
              </FormHelperText>
            )}
        </div>
      </FormControl>

      <div className="flex justify-end gap-3">
        <Button
          variant="outlined"
          size="md"
          onClick={props.onCancel}
          disabled={form.formState.isSubmitting || props.isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="md"
          loading={form.formState.isSubmitting || props.isLoading}
          disabled={
            !form.watch('description')?.trim() ||
            (form.watch('type') == 'pat' && !selectedDefaultWorkspace) ||
            (form.watch('type') === 'service' &&
              scopeOption === 'specific' &&
              selectedWorkspaces.length === 0)
          }
        >
          Create API Key
        </Button>
      </div>
    </form>
  );
}

export function CreateKeyModal(props: {
  className?: string;
  isWorkspaceAdmin: boolean;
  onSuccess?: (
    type: 'pat' | 'service',
    data: NewApiKeySchema | undefined
  ) => void;
  onTabChange?: (tab: 'pat' | 'service') => void;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const keyTypeRef = useRef<'pat' | 'service'>('pat');

  const createKey = useNewApiKeyMutation({
    onSuccess: (data) => {
      props.onSuccess?.(keyTypeRef.current, data);
      props.onTabChange?.(keyTypeRef.current);
      setOpen(false);
    },
  });

  const createOrgScopedServiceKey = useNewOrgScopedApiKeyMutation({
    onSuccess: (data) => {
      props.onSuccess?.('service', data);
      props.onTabChange?.('service');
      setOpen(false);
    },
  });

  const createOrgScopedPat = useNewOrgScopedPatMutation({
    onSuccess: (data) => {
      props.onSuccess?.('pat', data);
      props.onTabChange?.('pat');
      setOpen(false);
    },
  });

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(newOpen) => {
          setOpen(newOpen);
        }}
      >
        <DialogContent
          title="Create an API Key"
          className="max-h-[90vh] w-[800px] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="mx-auto my-4 flex w-full max-w-[800px] flex-col gap-8 transition-all duration-300 ease-in-out">
            {(createKey.error ||
              createOrgScopedServiceKey.error ||
              createOrgScopedPat.error) && (
              <ExpandableErrorAlert
                error={
                  createKey.error ||
                  createOrgScopedServiceKey.error ||
                  createOrgScopedPat.error
                }
              />
            )}

            <CreateKeyForm
              serviceKeysEnabled={props.isWorkspaceAdmin}
              isLoading={
                createKey.isMutating ||
                createOrgScopedServiceKey.isMutating ||
                createOrgScopedPat.isMutating
              }
              onCancel={() => setOpen(false)}
              onSubmit={({
                description,
                type,
                expires_at,
                scope,
                workspaces,
                role_id,
                org_role_id,
                default_workspace_id,
              }) => {
                keyTypeRef.current = type;

                if (type === 'service') {
                  const json: ApiKeyPayload = { description, expires_at };
                  if (role_id) {
                    json.role_id = role_id;
                  }
                  if (scope === 'full' && org_role_id) {
                    json.org_role_id = org_role_id;
                  } else if (scope === 'specific' && workspaces) {
                    json.workspaces = workspaces;
                  }
                  createOrgScopedServiceKey.trigger({ json });
                } else {
                  createOrgScopedPat.trigger({
                    json: { description, expires_at, default_workspace_id },
                  });
                }
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Button
        size="md"
        onClick={() => setOpen(true)}
        className={props.className}
        leftDecorator={PlusIcon}
      >
        API Key
      </Button>
    </>
  );
}
